import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from 'react';
import {
  favoritesService,
  FavoriteCaregiver,
  FavoriteCaregiverSummary,
} from '../services/favoritesService';
import { authService } from '../services/authService';
import { useApp } from './AppContext';

// FEAT-057 — Save / Favorite Caregivers (MVP-18.29). Holds the authed
// careseeker's favorited caregivers so the heart toggle on discovery/search
// cards and the caregiver profile can reflect state without prop drilling.
// Toggling is optimistic with a revert on API error. Unauthenticated users
// are gated at the FavoriteButton level (the provider stays empty for them).

interface FavoritesContextValue {
  favorites: FavoriteCaregiver[];
  loading: boolean;
  isFavorited: (caregiverId: string) => boolean;
  toggleFavorite: (
    caregiverId: string,
    summary?: FavoriteCaregiverSummary
  ) => Promise<boolean>;
  refresh: () => Promise<void>;
}

const FavoritesContext = createContext<FavoritesContextValue | undefined>(undefined);

interface FavoritesProviderProps {
  children: ReactNode;
}

export const FavoritesProvider: React.FC<FavoritesProviderProps> = ({ children }) => {
  const { state } = useApp();
  const [favorites, setFavorites] = useState<FavoriteCaregiver[]>([]);
  const [loading, setLoading] = useState(false);
  const favoritedIds = useMemo(
    () => new Set(favorites.map((favorite) => favorite.caregiverId)),
    [favorites]
  );

  const isAuthed = authService.isAuthenticated() && !!state.user;

  const refresh = useCallback(async () => {
    if (!authService.isAuthenticated()) {
      setFavorites([]);
      return;
    }
    try {
      setLoading(true);
      const list = await favoritesService.listFavorites();
      setFavorites(list);
    } catch (error) {
      console.error('Failed to load favorites:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load once the user is authenticated; clear when they log out.
  const loadedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (isAuthed && state.user?.id && loadedForRef.current !== state.user.id) {
      loadedForRef.current = state.user.id;
      void refresh();
    } else if (!isAuthed) {
      loadedForRef.current = null;
      setFavorites([]);
    }
  }, [isAuthed, state.user?.id, refresh]);

  const isFavorited = useCallback(
    (caregiverId: string) => favoritedIds.has(caregiverId),
    [favoritedIds]
  );

  const toggleFavorite = useCallback(
    async (caregiverId: string, summary?: FavoriteCaregiverSummary): Promise<boolean> => {
      const previous = favorites;
      const currentlyFavorited = previous.some(
        (favorite) => favorite.caregiverId === caregiverId
      );
      const nextFavorited = !currentlyFavorited;

      // Optimistic update.
      if (nextFavorited) {
        const optimistic: FavoriteCaregiver = {
          id: `optimistic-${caregiverId}`,
          caregiverId,
          createdAt: new Date().toISOString(),
          caregiver:
            summary ?? {
              id: caregiverId,
              firstName: null,
              lastName: null,
              avatar: null,
            },
        };
        setFavorites([optimistic, ...previous]);
      } else {
        setFavorites(previous.filter((favorite) => favorite.caregiverId !== caregiverId));
      }

      try {
        if (nextFavorited) {
          const created = await favoritesService.addFavorite(caregiverId);
          // Reconcile the optimistic row with the server payload.
          setFavorites((current) => [
            created,
            ...current.filter((favorite) => favorite.caregiverId !== caregiverId),
          ]);
        } else {
          await favoritesService.removeFavorite(caregiverId);
        }
        return nextFavorited;
      } catch (error) {
        console.error('Failed to toggle favorite:', error);
        // Revert on error.
        setFavorites(previous);
        throw error;
      }
    },
    [favorites]
  );

  const value = useMemo<FavoritesContextValue>(
    () => ({ favorites, loading, isFavorited, toggleFavorite, refresh }),
    [favorites, loading, isFavorited, toggleFavorite, refresh]
  );

  return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>;
};

export const useFavorites = (): FavoritesContextValue => {
  const context = useContext(FavoritesContext);
  if (context === undefined) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};
